import { ref, onScopeDispose, getCurrentScope } from 'vue'

/**
 * Reactive "is the browser extension installed?" flag.
 *
 * The extension's content script sets `data-linkflare-extension="installed"`
 * on `<html>` when it runs on a Linkflare page. It may load after Vue, so a
 * one-shot read at setup misses a freshly installed extension — hence the
 * MutationObserver.
 *
 * This logic previously existed as two verbatim copies (OnboardingFlow.vue and
 * Bookmarks.vue). It lives here now so every surface that reacts to the
 * extension — the onboarding step, the install prompt, the analytics
 * milestone — observes exactly the same signal.
 *
 * Reading the flag is pure: the `extension_install` milestone is only recorded
 * for the ONE caller that passes `{ track: true }` (ExtensionPrompt, which is
 * mounted for the whole authenticated session). Otherwise a future surface that
 * just wants to hide a menu item would emit an install as a side effect of
 * asking a question.
 */

const ATTRIBUTE = 'data-linkflare-extension'
// Set the first time we observe a browser WITHOUT the extension. Without it an
// already-installed extension is indistinguishable from one installed just now,
// so every pre-existing user would be counted as a fresh install on their next
// load. Absent marker + already installed = backfill, not an install.
const SEEN_ABSENT_KEY = 'lf:ext:seen-absent'

export function isExtensionInstalled() {
  if (typeof document === 'undefined') return false
  return document.documentElement.getAttribute(ATTRIBUTE) === 'installed'
}

export function useExtensionInstalled({ track = false } = {}) {
  const extensionInstalled = ref(isExtensionInstalled())

  if (track) {
    if (extensionInstalled.value) {
      // Only an install if we had previously seen this browser without it.
      if (readSeenAbsent()) trackInstall('load')
    } else {
      writeSeenAbsent()
    }
  }

  let observer = null
  if (!extensionInstalled.value && typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(() => {
      if (isExtensionInstalled()) {
        extensionInstalled.value = true
        // The attribute flipping mid-session means the install just happened.
        if (track) trackInstall('live')
        observer?.disconnect()
        observer = null
      }
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: [ATTRIBUTE] })
  }

  // onScopeDispose rather than onUnmounted so the composable is also safe to
  // call from a plain effect scope, not just component setup().
  if (getCurrentScope()) {
    onScopeDispose(() => {
      observer?.disconnect()
      observer = null
    })
  }

  return { extensionInstalled }
}

function readSeenAbsent() {
  try {
    return localStorage.getItem(SEEN_ABSENT_KEY) === '1'
  } catch (e) {
    // Storage blocked: we cannot tell backfill from a real install, so stay
    // silent rather than inflate the metric.
    return false
  }
}

function writeSeenAbsent() {
  try {
    localStorage.setItem(SEEN_ABSENT_KEY, '1')
  } catch (e) {
    // Same degradation as above.
  }
}

// Lazy so the analytics chunk stays off the startup path.
function trackInstall(detected) {
  import('../services/analytics.js')
    .then(m => m.trackOnce('extension_install', 'extension_install', { detected }))
    .catch(() => { })
}
